import type { NormalizedModuleConfig } from "./types";
import { ACTIVE_STATES, MAX_VISIBLE_MODULES } from "./constants";
import { EXCLUDED_MODULE_KEYS, MAIN_KEYS } from "./routes";

export interface OrderedModule {
  key: string;
  state: string;
  position: number;
  visible: boolean;
  /** Admin choice to hide the module from the member nav (see types.ts). */
  hidden: boolean;
}

export interface ModuleSplit {
  visibleModules: OrderedModule[];
  overflowModules: OrderedModule[];
}

/**
 * Splits a normalized module config into visible (top N) and overflow lists.
 *
 * Filters out inactive states (OFF, ARCHIVED, UNPUBLISHED), excluded keys
 * (VIDEO_EMBEDS, VIDEO_UPLOADS), main nav keys (POSTS, SETTINGS, etc.), and
 * modules an admin explicitly hid from the nav (`hidden`). Visible modules
 * are capped at MAX_VISIBLE_MODULES. Both lists are sorted by position
 * ascending.
 */
export function splitModules(config: NormalizedModuleConfig): ModuleSplit {
  const active = Object.entries(config)
    .filter(
      ([key, entry]) =>
        ACTIVE_STATES.has(entry.state) &&
        !EXCLUDED_MODULE_KEYS.has(key) &&
        !MAIN_KEYS.has(key) &&
        !entry.hidden,
    )
    .map(([key, entry]) => ({ key, ...entry }))
    .sort((a, b) => a.position - b.position);

  const visibleModules = active
    .filter((m) => m.visible)
    .slice(0, MAX_VISIBLE_MODULES);

  const visibleKeys = new Set(visibleModules.map((m) => m.key));
  const overflowModules = active.filter((m) => !visibleKeys.has(m.key));

  return { visibleModules, overflowModules };
}
